export const surgeryModule = {
  id: "surgery",
  title: "Adrenal Gland Surgery: Part 1",
  sections: [
    {
      type: "text",
      title: "Indications for Adrenalectomy",
      content: `
        <p><span class="glossary-term">Adrenalectomy</span> (surgical removal of one or both adrenal glands) is indicated for functional tumours, malignant tumours, and selected non-functional masses meeting size or imaging criteria.</p>

        <div class="highlight-box">
          <p><strong>Functional tumours — all require surgery (unless unfit)</strong></p>
          <ul>
            <li><span class="purple-term">Phaeochromocytoma / paraganglioma</span> — surgical resection is the only curative treatment; requires careful pre-operative pharmacological preparation (alpha then beta blockade — see pharmacology module)</li>
            <li><span class="purple-term">Primary hyperaldosteronism (Conn's syndrome)</span> from unilateral adrenal adenoma or unilateral hyperplasia — adrenalectomy is curative; bilateral disease is managed medically with spironolactone</li>
            <li><span class="purple-term">Cortisol-secreting adrenal adenoma</span> (ACTH-independent Cushing's syndrome) — unilateral adrenalectomy is curative; HPA axis recovery takes months post-op</li>
            <li><span class="purple-term">Androgen-secreting adrenal tumour</span> — if confirmed on imaging and biochemistry</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Malignant tumours</strong></p>
          <ul>
            <li><span class="purple-term">Adrenocortical carcinoma (ACC)</span> — radical resection is the primary treatment; requires open surgery due to risk of laparoscopic capsular rupture and peritoneal seeding; adjacent organs (kidney, liver, spleen) may require en bloc resection</li>
            <li><span class="purple-term">Adrenal metastases</span> — adrenalectomy may be offered to selected patients with isolated adrenal metastasis from a primary tumour with good prognosis (e.g. non-small cell lung cancer, renal cell carcinoma, melanoma)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Non-functional incidentalomas meeting criteria</strong></p>
          <ul>
            <li>Size ≥4 cm (higher malignancy risk — see incidentaloma section)</li>
            <li>Imaging features suspicious for malignancy regardless of size (heterogeneous, calcification, >10 HU, poor contrast washout)</li>
            <li>Growth on surveillance imaging (≥1 cm increase over 12 months)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Bilateral adrenalectomy — specific indications</strong></p>
          <ul>
            <li>Bilateral phaeochromocytoma (MEN2 / VHL) — staged or simultaneous bilateral adrenalectomy; cortex-sparing technique may be attempted to preserve adrenal function</li>
            <li>ACTH-independent bilateral macronodular adrenal hyperplasia (BMAH) causing Cushing's syndrome</li>
            <li>Ectopic ACTH syndrome from an inoperable primary — bilateral adrenalectomy eliminates Cushing's despite ongoing ACTH production</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A 42-year-old with hypertension resistant to three drugs is found to have a unilateral 1.8 cm right adrenal adenoma. Aldosterone:renin ratio is markedly elevated and adrenal venous sampling confirms right-sided aldosterone excess. What is the definitive treatment?",
      options: [
        "Lifelong spironolactone — medical management is first-line for all Conn's patients",
        "Right adrenalectomy — unilateral disease confirmed by adrenal venous sampling is surgically curable",
        "Bilateral adrenalectomy — both glands must be removed to prevent recurrence",
        "MRI-guided ablation of the adenoma — less invasive than surgery",
      ],
      correct: 1,
      explanation:
        "Primary hyperaldosteronism (Conn's syndrome) due to a unilateral aldosterone-producing adenoma (APA) is surgically curable. Adrenal venous sampling (AVS) is essential before surgery — it confirms the excess is truly unilateral, as bilateral adrenal hyperplasia (which requires medical therapy) cannot be reliably excluded on CT alone. After confirmed unilateral disease, laparoscopic adrenalectomy resolves hypokalaemia in virtually all patients, and 50–70% achieve complete cure of hypertension (remaining patients often have improved BP control on fewer drugs). Spironolactone is reserved for bilateral disease or patients unfit for surgery.",
    },
    {
      type: "text",
      title: "Adrenal Incidentaloma: Surgical Decision Pathway",
      content: `
        <p>An <span class="glossary-term">adrenal incidentaloma</span> is an adrenal mass found unexpectedly on imaging performed for another indication. Prevalence increases with age (~4% of adults on CT). The key questions are: is it functional? Is it malignant?</p>

        <div class="highlight-box">
          <p><strong>Mandatory biochemical evaluation for all incidentalomas</strong></p>
          <ul>
            <li><span class="purple-term">1 mg overnight dexamethasone suppression test</span> — exclude subclinical Cushing's (autonomous cortisol secretion is found in ~5–10% of incidentalomas)</li>
            <li><span class="purple-term">Plasma/urine metanephrines</span> — exclude phaeochromocytoma in ALL cases before any intervention, biopsy, or surgery; phaeochromocytoma can be normotensive and imaging cannot exclude it reliably</li>
            <li><span class="purple-term">Aldosterone:renin ratio</span> — only if hypertensive and/or hypokalaemic</li>
            <li>DHEA-S / androgens — if signs of virilisation or feminisation</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>CT imaging characteristics — distinguishing benign from malignant</strong></p>
          <ul>
            <li><strong>Lipid-rich adenoma (benign)</strong>: density <span class="purple-term">&lt;10 Hounsfield Units (HU)</span> on unenhanced CT → lipid-rich → almost certainly benign adenoma (PPV ~98%)</li>
            <li><strong>Contrast washout</strong> (if enhanced CT used):
              <ul>
                <li>Absolute percentage washout (APW) <strong>&gt;60%</strong> at 15 minutes = adenoma</li>
                <li>Relative percentage washout (RPW) <strong>&gt;40%</strong> at 15 minutes = adenoma</li>
              </ul>
            </li>
            <li><strong>Malignancy features</strong>: heterogeneous, irregular, calcification, necrosis, >10 HU, poor washout, local invasion or satellite lesions</li>
            <li><strong>Phaeochromocytoma on CT</strong>: often hyperdense (&gt;10 HU), heterogeneous, avidly enhancing with slow washout — do NOT biopsy; biochemistry must confirm or exclude first</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Decision to operate vs surveillance</strong></p>
          <ul>
            <li><strong>Operate</strong> if:
              <ul>
                <li>Any functional tumour (phaeochromocytoma, autonomous cortisol, aldosterone, androgens)</li>
                <li>Size ≥4 cm (risk of ACC increases significantly; ESE/ENSAT recommend resection)</li>
                <li>CT features suspicious for malignancy regardless of size</li>
                <li>Growth ≥1 cm over 12 months on surveillance</li>
              </ul>
            </li>
            <li><strong>Surveillance</strong> if:
              <ul>
                <li>Non-functional + <4 cm + benign CT features (&lt;10 HU, good washout)</li>
                <li>CT at 6–12 months; repeat biochemistry at 1 year; if stable, can discharge</li>
              </ul>
            </li>
            <li><strong>Do NOT biopsy</strong> an adrenal mass until phaeochromocytoma is biochemically excluded — biopsy of an unrecognised phaeochromocytoma can precipitate a fatal hypertensive crisis</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Adrenal Incidentaloma — Always Exclude Phaeochromocytoma First</div>
          <div class="mnemonic-content">Before ANY intervention (biopsy, adrenalectomy, embolisation), measure plasma/urine metanephrines. Even a normotensive patient can have a phaeochromocytoma. Triggering catecholamine release in an unblocked patient can be fatal.</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A 55-year-old patient has an incidentally found 3.2 cm adrenal mass on CT (unenhanced density 6 HU). Overnight dexamethasone suppression test is normal. Plasma metanephrines are normal. Blood pressure and potassium are normal. What is the most appropriate management?",
      options: [
        "Immediate laparoscopic adrenalectomy — any adrenal mass >3 cm should be removed",
        "CT-guided biopsy to confirm histology before deciding on surgery",
        "Surveillance with repeat CT and biochemistry at 6–12 months — non-functional, small, lipid-rich adenoma",
        "Refer for PET-CT scan to exclude malignancy before any decision",
      ],
      correct: 2,
      explanation:
        "This is a classic benign-appearing adrenal incidentaloma: <4 cm, non-functional on biochemistry (no Cushing's, no phaeochromocytoma, no hyperaldosteronism), and lipid-rich on CT (6 HU — well below the 10 HU threshold for lipid-rich adenoma). Surgery is not indicated. The appropriate management is surveillance: repeat imaging at 6–12 months to check for growth, and repeat biochemistry at 12 months to detect delayed functional evolution. Biopsy would only be appropriate if malignancy is suspected and phaeochromocytoma has already been excluded biochemically.",
    },
    {
      type: "question",
      text: "An adrenal mass measuring 5.5 cm is found incidentally. CT shows heterogeneous texture, calcification, and 22 HU on unenhanced imaging. Biochemistry confirms non-function. Phaeochromocytoma is excluded. What is the next step?",
      options: [
        "Surveillance — the mass is non-functional so monitoring is safe",
        "CT-guided biopsy to establish histology before planning management",
        "Surgical resection — size and imaging features raise significant concern for adrenocortical carcinoma",
        "Medical treatment with mitotane as first-line for suspected ACC",
      ],
      correct: 2,
      explanation:
        "Size ≥4 cm with suspicious CT features (heterogeneous, calcification, >10 HU, poor washout) is highly concerning for adrenocortical carcinoma (ACC). ACC most commonly presents as a large non-functional mass. The correct approach is surgical resection — ideally open adrenalectomy (laparoscopic approach risks capsular rupture and peritoneal seeding in ACC). Biopsy is generally avoided pre-operatively as it risks seeding. Staging CT of chest/abdomen/pelvis should be performed, and management discussed by a multidisciplinary team. Mitotane is adjuvant/palliative, not first-line before resection.",
    },
    {
      type: "text",
      title: "Surgical Approaches to Adrenalectomy",
      content: `
        <p>The choice of surgical approach depends on tumour size, suspected pathology, patient factors, and available expertise. Minimally invasive approaches are preferred for most benign adrenal pathology.</p>

        <div class="highlight-box">
          <p><strong>Laparoscopic transperitoneal adrenalectomy</strong></p>
          <ul>
            <li>The most widely used approach for benign adrenal tumours</li>
            <li>Patient positioned laterally (flank); peritoneal cavity entered; 3–4 ports; excellent visualisation of surrounding structures</li>
            <li>Advantages: less pain, shorter hospital stay, quicker recovery than open</li>
            <li>Suitable for: phaeochromocytoma ≤6–8 cm (after pharmacological preparation), Conn's adenoma, Cushing's adenoma, incidentaloma meeting criteria</li>
            <li>Limitations: large tumours (&gt;6–8 cm), ACC (see below), previous abdominal surgery</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Posterior retroperitoneoscopic adrenalectomy (PRA)</strong></p>
          <ul>
            <li>Prone position; retroperitoneal space accessed via the back — does not enter the peritoneal cavity</li>
            <li>Advantages: direct access to adrenal gland; no bowel mobilisation; ideal for bilateral adrenalectomy (no repositioning needed); suitable after previous abdominal surgery</li>
            <li>Limitations: smaller working space; less suitable for very large tumours</li>
            <li>Increasingly preferred in specialist centres for most unilateral and bilateral adrenalectomies</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Open adrenalectomy</strong></p>
          <ul>
            <li>Required for:
              <ul>
                <li><span class="purple-term">Adrenocortical carcinoma</span> — laparoscopic approach is contraindicated due to risk of capsular disruption and peritoneal seeding; requires en bloc resection with potential nephrectomy, splenectomy, or hepatic resection if involved</li>
                <li>Large phaeochromocytoma/paraganglioma (&gt;6–8 cm or with suspected local invasion)</li>
                <li>Adrenal tumours invading adjacent structures (IVC, kidney, liver)</li>
                <li>Conversion from laparoscopic if bleeding or poor visualisation</li>
              </ul>
            </li>
            <li>Approach: subcostal, flank, or midline incision depending on tumour and surgeon preference</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Cortex-sparing adrenalectomy</strong></p>
          <ul>
            <li>Technique: tumour-selective resection preserving a rim of normal adrenal cortex</li>
            <li>Indication: bilateral phaeochromocytoma (e.g. MEN2, VHL) — avoids permanent adrenal insufficiency by preserving cortical tissue from at least one gland</li>
            <li>Risk: 10–20% local recurrence rate (phaeochromocytoma in the preserved cortex)</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A surgeon plans laparoscopic adrenalectomy for a 6 cm adrenal mass with CT features suggesting adrenocortical carcinoma. A colleague disagrees with this approach. Who is correct, and why?",
      options: [
        "The surgeon is correct — laparoscopic surgery is safer and has equal oncological outcomes to open surgery for all adrenal tumours",
        "The colleague is correct — laparoscopic adrenalectomy is contraindicated in suspected ACC due to risk of capsular rupture, tumour fragmentation, and peritoneal seeding, which significantly worsens prognosis",
        "The colleague is correct — all adrenal tumours >5 cm must be approached open regardless of histology",
        "The surgeon is correct — laparoscopic is only contraindicated if the tumour is >10 cm",
      ],
      correct: 1,
      explanation:
        "Open adrenalectomy is mandatory for suspected or confirmed ACC. Laparoscopic adrenalectomy for ACC carries a significant risk of capsular disruption, positive surgical margins, tumour fragmentation, and peritoneal seeding — all of which dramatically worsen prognosis. ACC frequently requires en bloc resection of adjacent structures (kidney, spleen, hepatic segment, IVC), which is not safely achievable laparoscopically. Current European guidelines (ENSAT/ESE) recommend open surgery as the standard of care for any adrenal mass where ACC cannot be excluded. A clear resection margin (R0) is the single most important prognostic factor in ACC.",
    },
    {
      type: "text",
      title: "Structures at Risk During Adrenalectomy",
      content: `
        <p>The adrenal glands have intimate relationships with major vascular structures and surrounding organs. These vary significantly between the right and left sides and must be respected intraoperatively.</p>

        <div class="highlight-box">
          <p><strong>Right adrenalectomy — vascular danger zone</strong></p>
          <ul>
            <li><span class="purple-term">Right adrenal vein</span>: short (0.5–1 cm) and drains <strong>directly into the IVC</strong> — the single most dangerous step; tearing the right adrenal vein can cause rapid, uncontrolled IVC haemorrhage</li>
            <li><strong>Inferior vena cava (IVC)</strong>: lies immediately medial; must be carefully dissected and protected</li>
            <li><strong>Right hepatic vein</strong>: risk during superior dissection and liver retraction</li>
            <li><strong>Liver</strong>: right adrenal gland tucked behind the right lobe; liver retraction needed; hepatic laceration is a recognised complication</li>
            <li><strong>Duodenum</strong>: at risk during lower pole dissection</li>
            <li><strong>Right renal vein</strong>: inferior; at risk during adrenal vein dissection</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Left adrenalectomy — longer vein, more mobile side</strong></p>
          <ul>
            <li><span class="purple-term">Left adrenal vein</span>: longer; drains into the <strong>left renal vein</strong> (not directly into IVC); easier to identify and ligate than the right, but the left renal vein and IVC are still at risk</li>
            <li><strong>Spleen</strong>: immediately lateral — splenic laceration or inadvertent splenectomy can occur</li>
            <li><strong>Pancreatic tail</strong>: the tail of the pancreas is directly anterior to the left adrenal gland; pancreatic injury → pancreatitis or pancreatic fistula</li>
            <li><strong>Left renal vein</strong>: the left adrenal vein enters it superiorly</li>
            <li><strong>Aorta</strong>: medial; risk during extensive medial dissection</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Bilateral structures at risk (both sides)</strong></p>
          <ul>
            <li><strong>Kidneys and ureters</strong> — inferior; particularly at risk in large tumours with inferior extension</li>
            <li><strong>Diaphragm</strong> — superior; large tumours can extend superiorly; pneumothorax is a rare complication</li>
            <li><strong>Lumbar vessels</strong> — during posterior or retroperitoneal approaches</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Right vs Left Adrenal Vein — Key Difference</div>
          <div class="mnemonic-content"><strong>Right adrenal vein → IVC directly</strong> (short, dangerous, clip carefully)<br/><strong>Left adrenal vein → Left renal vein → then IVC</strong> (longer, more accessible, but left renal vein still at risk)<br/>The right is the "widow-maker" step — most adrenal haemorrhage disasters happen on the right.</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "During a right laparoscopic adrenalectomy for a 3 cm phaeochromocytoma, there is sudden significant haemorrhage when dissecting the medial aspect of the gland. What is the most likely source of bleeding?",
      options: [
        "The right hepatic vein — it runs immediately superior to the right adrenal gland",
        "The right adrenal vein — it is very short and drains directly into the IVC, and tearing it can cause rapid IVC haemorrhage",
        "The right renal artery — it passes posterior to the adrenal gland",
        "The inferior phrenic artery — the main arterial supply to the right adrenal gland",
      ],
      correct: 1,
      explanation:
        "The right adrenal vein is the most feared structure in right adrenalectomy. It is typically only 0.5–1 cm long and drains directly into the IVC — there is almost no margin for error. Tearing or avulsing this vein can cause rapid, massive haemorrhage from the IVC. Surgeons typically clip or ligate it early in the dissection (before mobilising the gland) to avoid traction. In contrast, the left adrenal vein is longer and drains into the left renal vein, making it technically more forgiving. Conversion to open surgery may be necessary to control IVC haemorrhage.",
    },
    {
      type: "question",
      text: "During a left adrenalectomy, the surgeon notices turbid, amylase-rich fluid in the drain 24 hours post-operatively. What structure was most likely injured?",
      options: [
        "The spleen — splenic injury causes bloody (not amylase-rich) fluid",
        "The tail of the pancreas — it lies directly anterior to the left adrenal gland; injury causes a pancreatic fistula with amylase-rich drain fluid",
        "The left renal vein — produces urine-like fluid with elevated creatinine, not amylase",
        "The aorta — aortic injury would cause haemorrhagic drain output",
      ],
      correct: 1,
      explanation:
        "The tail of the pancreas lies directly anterior to the left adrenal gland and is at risk during left adrenalectomy, particularly for larger lesions or those with anterior extension. Pancreatic injury results in a pancreatic fistula — characterised by amylase-rich drain fluid (typically >3× serum amylase). Management: keep the drain in situ, nil by mouth or low-fat enteral feeding, somatostatin analogues (octreotide) to reduce pancreatic secretion; most resolve conservatively. Persistent fistulae may require ERCP-guided stenting or surgical repair.",
    },
  ],
};
